import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { getApiErrorMessage, useApiGet } from '../../../utils/httpClientTs';
import { getPortalLoanHome } from '../../../endpoints/portalLoans';
import type { PortalLoanHomeResponse } from '../../../types/portalLoans';
import { formatPortalCurrency, formatPortalDate } from '../mock/store';
import {
  ELF_TEAL,
  elfOutlinedButtonSx,
  elfPrimaryButtonSx,
  elfTextButtonSx,
} from '../elfTheme';

const statusChipLabel = (paymentStatus: string): string => {
  if (paymentStatus === 'late') return 'Late';
  if (paymentStatus === 'on_time') return 'Current';
  return paymentStatus || '—';
};

const scheduleHeadline = (scheduleStatus: string): string => {
  if (scheduleStatus === 'behind') return 'Behind schedule';
  if (scheduleStatus === 'on_schedule') return 'On schedule';
  return scheduleStatus || '—';
};

/** Caption under schedule headline; chip already shows Current/Late. */
const scheduleCaption = (home: PortalLoanHomeResponse): string => {
  const pi = home.payment_info;
  const parts: string[] = [];
  if (pi.schedule_status === 'behind') {
    if (pi.catch_up_total && pi.catch_up_total > 0) {
      parts.push(`${formatPortalCurrency(pi.catch_up_total)} to catch up`);
    } else {
      parts.push('Contact ELF for help getting on schedule');
    }
  } else if (pi.schedule_status === 'on_schedule') {
    parts.push('Principal schedule is up to date');
  }
  if (pi.payment_status === 'late') {
    if (pi.days_late && pi.days_late > 0) {
      parts.push(
        `${pi.days_late} day${pi.days_late === 1 ? '' : 's'} past due`
      );
    } else {
      parts.push('Payment is past due');
    }
  }
  return parts.join('. ') || 'Payment schedule';
};

const isActiveRecurring = (status?: string | null): boolean =>
  (status || '').trim().toUpperCase() === 'ACTIVE';

const autopayCaption = (home: PortalLoanHomeResponse): string | null => {
  const r = home.payment_info.recurring;
  if (!r) return null;
  const bits: string[] = ['Autopay'];
  if (r.amount != null && !Number.isNaN(r.amount)) {
    bits.push(formatPortalCurrency(r.amount));
  }
  if (r.day_of_month != null) {
    bits.push(`on day ${r.day_of_month}`);
  }
  if (r.account_last4) {
    bits.push(`····${r.account_last4}`);
  }
  if (r.status) {
    bits.push(`(${r.status})`);
  }
  return bits.join(' · ');
};

const LoanHome: React.FC = () => {
  const displayName = localStorage.getItem('first_name') || 'there';
  const { data, isLoading, isError, error, refetch } = useApiGet(
    ['portal-loans-me'],
    getPortalLoanHome,
    { retry: 1 }
  );

  if (isLoading && !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: ELF_TEAL }} />
      </Box>
    );
  }

  if (isError && !data) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            Retry
          </Button>
        }
      >
        {getApiErrorMessage(error) || 'Unable to load your loan account.'}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info">
        No ELF loan account was found for your user.
      </Alert>
    );
  }

  const chipLabel = statusChipLabel(data.payment_info.payment_status);
  const nextAmt = data.payment_info.next_payment_amount;
  const autopay = autopayCaption(data);
  const accrued = data.loan_info.accrued_balance;
  const scheduleLabel = scheduleHeadline(data.payment_info.schedule_status);
  const scheduleNote = scheduleCaption(data);
  const hasActiveAutopay = isActiveRecurring(
    data.payment_info.recurring?.status
  );

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Hello, {displayName}
          </Typography>
          <Typography color="text.secondary">
            {data.program_label}
            {data.account_label ? ` · Account ${data.account_label}` : ''}
          </Typography>
        </Box>
        <Chip
          label={chipLabel}
          variant="outlined"
          color={
            data.payment_info.payment_status === 'late' ? 'error' : 'default'
          }
          sx={
            data.payment_info.payment_status === 'late'
              ? undefined
              : {
                  borderColor: ELF_TEAL,
                  color: ELF_TEAL,
                  fontWeight: 600,
                }
          }
        />
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(3, minmax(0, 1fr))',
          },
          gap: 2,
          mb: 3,
        }}
      >
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Current balance
          </Typography>
          <Typography variant="h4" component="p" sx={{ color: ELF_TEAL }}>
            {formatPortalCurrency(data.loan_info.total_balance)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Principal {formatPortalCurrency(data.loan_info.principal_balance)} ·
            Interest {formatPortalCurrency(data.loan_info.interest_balance)}
          </Typography>
          {accrued > 0.004 && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Accrued {formatPortalCurrency(accrued)}
            </Typography>
          )}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Next payment
          </Typography>
          <Typography variant="h4" component="p">
            {nextAmt != null ? formatPortalCurrency(nextAmt) : '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Due {formatPortalDate(data.payment_info.next_payment_due)}
          </Typography>
          {autopay && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              {autopay}
            </Typography>
          )}
          {hasActiveAutopay && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              To cancel autopay, contact ELF.
            </Typography>
          )}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Payment schedule
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{
              mt: 0.5,
              color:
                data.payment_info.schedule_status === 'behind'
                  ? 'error.main'
                  : undefined,
            }}
          >
            {scheduleLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {scheduleNote}
          </Typography>
        </Paper>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 4 }}
      >
        <Button
          component={RouterLink}
          to="/portal/loans/pay"
          variant="contained"
          size="large"
          sx={elfPrimaryButtonSx}
        >
          Make a payment
        </Button>
        {!hasActiveAutopay && (
          <Button
            component={RouterLink}
            to="/portal/loans/autopay"
            variant="outlined"
            size="large"
            sx={elfOutlinedButtonSx}
          >
            Set up autopay
          </Button>
        )}
      </Stack>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mb: 1 }}
      >
        <Typography variant="h6">Recent activity</Typography>
        <Button
          component={RouterLink}
          to="/portal/loans/history"
          variant="text"
          size="small"
          sx={elfTextButtonSx}
        >
          View full history
        </Button>
      </Stack>
      <Paper variant="outlined">
        {data.recent_activity.length === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No recent payments to show.
            </Typography>
          </Box>
        ) : (
          data.recent_activity.map((item, index) => (
            <Box
              key={item.id}
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 2,
                borderBottom: index < data.recent_activity.length - 1 ? 1 : 0,
                borderColor: 'divider',
              }}
            >
              <Box>
                <Typography variant="body2">{item.description}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatPortalDate(item.date)} · {item.status}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>
                {formatPortalCurrency(item.amount)}
              </Typography>
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
};

export default LoanHome;
